import { useEffect, useRef } from 'react';
import type { KeyState } from '@lexicon/core';
import { hapticKeyTap } from '../lib/haptics';
import { useStrings } from '../state/AppContext';
import { BackspaceIcon } from './icons';

type KeyDef =
  | { kind: 'letter'; letter: string }
  | { kind: 'backspace' }
  | { kind: 'enter' };

const letters = (row: string): KeyDef[] =>
  row.split('').map((letter): KeyDef => ({ kind: 'letter', letter }));

const ROWS: KeyDef[][] = [
  letters('qwertyuiop'),
  letters('asdfghjkl'),
  [{ kind: 'backspace' }, ...letters('zxcvbnm'), { kind: 'enter' }],
];

const STATE_CLASS: Record<KeyState, string> = {
  unused: '',
  absent: 'key--absent',
  present: 'key--present',
  correct: 'key--correct',
};

/**
 * Elements that own their own keystrokes. `[role="dialog"]` is in the list
 * because an open sheet takes focus, so every sheet in the app swallows
 * physical typing without the game screen having to know about it.
 */
const TYPING_TARGETS =
  'button, a, input, textarea, select, [contenteditable="true"], [role="dialog"]';

export interface KeyboardProps {
  keys: Record<string, KeyState>;
  onLetter: (letter: string) => void;
  onBackspace: () => void;
  onEnter: () => void;
  disabled?: boolean;
}

export function Keyboard({ keys, onLetter, onBackspace, onEnter, disabled = false }: KeyboardProps) {
  const t = useStrings();
  const handlers = useRef({ onLetter, onBackspace, onEnter, disabled });
  handlers.current = { onLetter, onBackspace, onEnter, disabled };

  // On-screen presses only. A physical keystroke has no finger on the glass
  // to feel the buzz, and the hardware-keyboard case on iOS is rare enough
  // not to be worth one.
  const press = (action: () => void) => () => {
    hapticKeyTap();
    action();
  };

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      const current = handlers.current;
      if (current.disabled) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      if (target?.closest?.(TYPING_TARGETS)) return;

      if (event.key === 'Enter') {
        event.preventDefault();
        if (event.repeat) return;
        current.onEnter();
        return;
      }
      if (event.key === 'Backspace') {
        event.preventDefault();
        if (event.repeat) return;
        current.onBackspace();
        return;
      }
      if (/^[a-zA-Z]$/.test(event.key)) {
        current.onLetter(event.key.toLowerCase());
      }
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <div role="group" aria-label={t.header.keyboard} className="flex flex-col gap-[2px] sm:gap-1.5">
      {ROWS.map((row, index) => (
        <div key={index} className="flex justify-center gap-1 sm:gap-1.5">
          {row.map((key) => {
            if (key.kind === 'backspace') {
              return (
                <button
                  key="backspace"
                  type="button"
                  data-key="backspace"
                  aria-label={t.header.backspace}
                  className="key key--wide"
                  disabled={disabled}
                  onClick={press(onBackspace)}
                >
                  <BackspaceIcon className="h-5 w-5" />
                </button>
              );
            }
            if (key.kind === 'enter') {
              return (
                <button
                  key="enter"
                  type="button"
                  data-key="enter"
                  className="key key--wide key--enter"
                  disabled={disabled}
                  onClick={press(onEnter)}
                >
                  {t.header.enter}
                </button>
              );
            }
            const state = keys[key.letter] ?? 'unused';
            return (
              <button
                key={key.letter}
                type="button"
                data-key={key.letter}
                data-state={state}
                aria-label={state === 'unused'
                  ? key.letter.toUpperCase()
                  : t.header.keyLabel(key.letter.toUpperCase(), t.header.keyState[state])}
                className={['key', STATE_CLASS[state]].filter(Boolean).join(' ')}
                disabled={disabled}
                onClick={press(() => onLetter(key.letter))}
              >
                {key.letter.toUpperCase()}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}
